import { ethers } from "ethers";
import * as bech32 from "bech32";
import {
  getReferralConfig,
  REFERRAL_CODE,
  EVM_INTERFACE_ABI,
} from "../referralConfig";
import { executeWithGasEstimation } from "./tradingEvmService";
import { clearPendingReferralCode } from "../../utils/referralParams";

/**
 * Convert a 0x EVM address to nibi... bech32 format.
 * The keeper GraphQL API requires bech32 for trader lookups.
 */
export const evmToNibiAddress = (evmAddress) => {
  const checksummed = ethers.getAddress(evmAddress);
  const addrBytes = Buffer.from(checksummed.slice(2), "hex");
  return bech32.encode("nibi", bech32.toWords(addrBytes));
};

/**
 * Convert a nibi... bech32 address to 0x EVM format.
 */
export const nibiToEvmAddress = (nibiAddress) => {
  const decoded = bech32.decode(nibiAddress);
  const addrBytes = Buffer.from(bech32.fromWords(decoded.words));
  return ethers.getAddress("0x" + addrBytes.toString("hex"));
};

/**
 * Parse a timestamp string, handling missing timezone (UTC assumed).
 */
const parseTimestamp = (value) => {
  if (!value) return 0;
  const a = Date.parse(value);
  if (Number.isFinite(a)) return a;
  const b = Date.parse(`${value}Z`);
  return Number.isFinite(b) ? b : 0;
};

/**
 * Query the keeper GraphQL API for the trader's active referral, given the
 * canonical nibi (bech32) trader address. Returns the latest active referral or null.
 */
export const getActiveReferralByNibi = async (nibiAddress) => {
  const config = getReferralConfig();
  const trader = nibiAddress;

  const query = `
    query ReferralForTrader($trader: String!) {
      referral {
        referralRedemptionTrader(trader: $trader) {
          code
          active
          referrer
          block { block_ts }
        }
      }
    }
  `;

  const res = await fetch(config.keeperUrl, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ query, variables: { trader } }),
  });

  const json = await res.json();
  if (!res.ok || json.errors) {
    throw new Error(
      `Referral query failed: ${JSON.stringify(json.errors ?? json)}`
    );
  }

  const events =
    json.data?.referral?.referralRedemptionTrader?.filter(
      (e) => e?.code
    ) ?? [];

  const active = events.filter((e) => e.active);
  if (active.length === 0) return null;

  active.sort(
    (a, b) =>
      parseTimestamp(a.block?.block_ts) - parseTimestamp(b.block?.block_ts)
  );

  const latest = active[active.length - 1];
  return {
    code: String(latest.code).toUpperCase(),
    referrer: latest.referrer,
    redeemedAt: latest.block?.block_ts ?? null,
  };
};

/**
 * Query the keeper for a trader's active referral, given a 0x EVM address.
 * (Converts to nibi then delegates.)
 */
export const getActiveReferral = async (evmAddress) => {
  return getActiveReferralByNibi(evmToNibiAddress(evmAddress));
};

/**
 * Submit the referral redeem transaction via the EVM interface contract.
 * Uses the signer already connected to the user's wallet (MetaMask / Bybit etc).
 */
export const redeemReferralCode = async (signer, code = REFERRAL_CODE) => {
  const config = getReferralConfig();

  const contract = new ethers.Contract(
    config.evmInterfaceAddress,
    EVM_INTERFACE_ABI,
    signer
  );

  const wasmMsg = {
    redeem_referrer_code: {
      code: code.trim().toUpperCase(),
    },
  };

  const wasmMsgBytes = ethers.toUtf8Bytes(JSON.stringify(wasmMsg));

  const tx = await executeWithGasEstimation(contract, "executeSimpleFunctions", [
    wasmMsgBytes,
  ]);
  return tx.wait();
};

const REFERRAL_STORAGE_KEY = "estate_referral_applied";

/**
 * In-flight guard: dedupe concurrent enforcement calls for the same address so
 * multiple callers (per-connect handler + the global enforcer) can never trigger
 * two keeper queries or two redeem-tx prompts. Concurrent callers share one promise.
 */
const _inFlight = new Map();
const dedupeByAddress = (address, fn) => {
  const key = String(address || "").toLowerCase();
  if (!key) return fn();
  if (_inFlight.has(key)) return _inFlight.get(key);
  const p = (async () => {
    try {
      return await fn();
    } finally {
      _inFlight.delete(key);
    }
  })();
  _inFlight.set(key, p);
  return p;
};

/**
 * Check localStorage to see if this address is already CONFIRMED on ESTATE.
 * Prevents repeated keeper queries / sign-tx prompts caused by indexer delay.
 * Only set once we know the active code is ESTATE — never for other codes.
 */
const isOnEstate = (evmAddress) => {
  try {
    const stored = JSON.parse(localStorage.getItem(REFERRAL_STORAGE_KEY) || "{}");
    return !!stored[evmAddress.toLowerCase()];
  } catch {
    return false;
  }
};

const markOnEstate = (evmAddress) => {
  try {
    const stored = JSON.parse(localStorage.getItem(REFERRAL_STORAGE_KEY) || "{}");
    stored[evmAddress.toLowerCase()] = Date.now();
    localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // non-critical
  }
};

/**
 * Enforce the ESTATE referral code for every wallet on this platform.
 *
 * Policy (decided by product): every wallet must be on ESTATE so Coded Estate
 * earns the referral fee. We IGNORE any ?ref= param — regardless of where the user
 * came from, we put them on ESTATE.
 *
 * Flow:
 *   - If we've already confirmed this wallet is on ESTATE (localStorage) → no-op.
 *   - Query the keeper for the active code:
 *       · already ESTATE → cache + skip (no tx).
 *       · any other code OR no code → redeem ESTATE (overwrites; the contract allows
 *         re-redeeming a different code — last write wins).
 *
 * Returns { status, currentCode?, referrer?, txHash?, code? }.
 */
export const ensureEstateReferral = (signer, evmAddress) =>
  dedupeByAddress(evmAddress, () => _ensureEstateReferral(signer, evmAddress));

const _ensureEstateReferral = async (signer, evmAddress) => {
  // Any pending ?ref= code is intentionally discarded — we only ever use ESTATE.
  clearPendingReferralCode();

  if (isOnEstate(evmAddress)) {
    return { status: "already_applied" };
  }

  const existing = await getActiveReferral(evmAddress);

  // Already on ESTATE → nothing to do, cache it so we don't re-query.
  if (existing && existing.code === REFERRAL_CODE) {
    markOnEstate(evmAddress);
    return {
      status: "skipped",
      currentCode: existing.code,
      referrer: existing.referrer,
    };
  }

  // No code, or on a non-ESTATE code → redeem ESTATE (overwrites any existing code).
  const receipt = await redeemReferralCode(signer, REFERRAL_CODE);
  markOnEstate(evmAddress);

  return {
    status: existing ? "switched" : "redeemed",
    previousCode: existing?.code ?? null,
    txHash: receipt?.hash,
    code: REFERRAL_CODE,
  };
};

/**
 * Enforce the ESTATE referral code for a COSMOS (native nibi) wallet — Keplr/Leap.
 *
 * Same policy as the EVM path: every wallet must be on ESTATE. We ignore ?ref=, and
 * if the wallet isn't already on ESTATE we redeem it via the perp contract (the
 * contract allows re-redeeming a different code — last write wins).
 *
 * @param {string} nibiAddress - the connected nibi1... account
 * @param {string} walletType  - "keplr" | "leap" (window key used by executePerps)
 * @param {function} dispatch   - redux dispatch (executePerps needs it)
 */
export const ensureEstateReferralCosmos = (nibiAddress, walletType, dispatch) =>
  dedupeByAddress(nibiAddress, () =>
    _ensureEstateReferralCosmos(nibiAddress, walletType, dispatch)
  );

const _ensureEstateReferralCosmos = async (nibiAddress, walletType, dispatch) => {
  if (!nibiAddress || !nibiAddress.startsWith("nibi")) return { status: "skipped" };

  clearPendingReferralCode(); // ?ref= is discarded — always ESTATE

  if (isOnEstate(nibiAddress)) {
    return { status: "already_applied" };
  }

  const existing = await getActiveReferralByNibi(nibiAddress);

  if (existing && existing.code === REFERRAL_CODE) {
    markOnEstate(nibiAddress);
    return { status: "skipped", currentCode: existing.code, referrer: existing.referrer };
  }

  // Redeem ESTATE directly on the perp contract (no funds attached).
  const { executePerps } = await import("../../Components/functions/Contract");
  const { getNibiruSDK, getContractAddresses } = await import("../../config/network");
  const nibiru = getNibiruSDK();
  const message = { redeem_referrer_code: { code: REFERRAL_CODE } };

  const hash = await executePerps(
    nibiru.chainId,
    nibiru.endptTm,
    getContractAddresses().perp,
    dispatch,
    message,
    nibiAddress,
    walletType
  );
  markOnEstate(nibiAddress);

  return {
    status: existing ? "switched" : "redeemed",
    previousCode: existing?.code ?? null,
    txHash: hash,
    code: REFERRAL_CODE,
  };
};
